'use client';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ContentText from './ContentText';
import Tag from '../molecules/Tag';
import { MdCheck } from 'react-icons/md';
import { BREAKPOINT_SP, OptionValue } from '@/util/const';
import { UseFormRegisterReturn } from 'react-hook-form';
import { InputTextNoError } from './input/InputText';
import Select, { components, OptionProps } from 'react-select';

export type OptionType = {
  label: string;
  value: string;
};
type Props = {
  label?: string;
  guide?: string;
  required?: boolean;
  options: OptionType[];
  initValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  $layout?: 'horizontal' | 'vertical';
  $size?: 'L' | 'M' | 'S';
  placeholder?: string;
  icon?: React.ReactNode;
  $modeRed?: boolean;
  disabled?: boolean;
  register?: UseFormRegisterReturn<any>;
  errorMessage?: string;
  isClearable?: boolean;
  isHiddenOnMobile?: boolean;
  isMulti?: boolean;
  id?: string;
};

const Picklist: React.FC<Props> = (props: Props) => {
  const layout = props.$layout || 'horizontal';
  const size = props.$size || 'S';
  const [selectedValue, setSelectedValue] = useState(
    props.initValue || OptionValue.EMPTY,
  );
  useEffect(() => {
    setSelectedValue(props.value || props.initValue || OptionValue.EMPTY);
  }, [props.value, props.initValue]);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const [active, setActive] = useState(Boolean(props.initValue));
  const modeRed = props.$modeRed || false;
  const handleSelectChange = (newValue: string) => {
    setSelectedValue(newValue);
    setActive(Boolean(newValue));
    props.onChange && props.onChange(newValue);
    props.register?.onChange({ target: { value: newValue } });
  };
  const SelectOptionWithIcon = (props: OptionProps<any, false>) => {
    return (
      <components.Option
        {...props}
        className={`custom-option ${props.isSelected ? 'selected' : ''}`}
      >
        <ContentText>{props.data.label}</ContentText>
        {props.isSelected && <MdCheck className="icon" size={20} />}
      </components.Option>
    );
  };
  const commonConfig = useMemo(() => {
    return {
      isSearchable: false,
      options: props.options,
      isDisabled: props.disabled,
      placeholder: props.placeholder ? props.placeholder : '未選択',
      defaultValue: props.options.filter(
        (it) => selectedValue.indexOf(it.value) > -1,
      ),
      classNames: {
        container: () => 'container',
        control: () => `control ${active && modeRed ? 'active' : ''}`,
        menu: () =>
          props.icon || (active && modeRed) ? 'menu padding' : 'menu',
        indicatorSeparator: () => 'separator',
        singleValue: () => 'singleValue',
        indicatorsContainer: () =>
          `indicatorsContainer ${
            props.isHiddenOnMobile ? 'hidden-on-mobile' : ''
          }`,
        option: (option: { isSelected: boolean }) => {
          if (option.isSelected) {
            return 'option selected';
          }
          return 'option';
        },
      },
      isClearable: props.isClearable,
    };
  }, [props, selectedValue, active]);

  return (
    <>
      {mounted && (
        <Container $layout={layout} className={props.className} id={props.id}>
          {props.label && (
            <LabelWrapper>
              <ContentText type="label-l">{props.label}</ContentText>
              {props.required && (
                <Tag theme="error" small>
                  必須
                </Tag>
              )}
            </LabelWrapper>
          )}
          {props.guide && (
            <ContentText
              type="article-m"
              color="var(--color-dark-gray, #585757)"
            >
              {props.guide}
            </ContentText>
          )}
          <SelectWrapper
            $layout={layout}
            $size={size}
            active={props.$modeRed && active}
            $modeRed={modeRed}
            className={props.isHiddenOnMobile ? 'hidden-on-mobile' : ''}
            disabled={props.disabled}
          >
            {props.icon && <div className="icon">{props.icon}</div>}
            {active && modeRed && (
              <ButtonContainer $layout={props.$layout} $size={props.$size}>
                <MdCheck size={24} />
              </ButtonContainer>
            )}
            {props.isMulti ? (
              <Select
                isMulti
                onChange={(v, a) => {
                  handleSelectChange(v.map((it) => it.value).join(';'));
                }}
                {...commonConfig}
              />
            ) : (
              <Select
                components={{ Option: SelectOptionWithIcon }}
                onChange={(v, a) => {
                  if (v?.value == selectedValue && props.isClearable != false) {
                    handleSelectChange(OptionValue.EMPTY);
                  } else {
                    handleSelectChange(v?.value || OptionValue.EMPTY);
                  }
                }}
                value={
                  props.options.find((it) => it.value == selectedValue) || null
                }
                {...commonConfig}
              />
            )}
          </SelectWrapper>
          {props.errorMessage && (
            <InputTextNoError>{props.errorMessage}</InputTextNoError>
          )}
        </Container>
      )}
    </>
  );
};

type SelectWrapperProps = {
  $layout?: 'horizontal' | 'vertical';
  $size?: 'L' | 'M' | 'S';
  active?: boolean;
  $modeRed?: false | true;
  disabled?: boolean;
};

const SelectWrapper = styled.div<SelectWrapperProps>`
  gap: 0px;
  display: flex;
  background: ${(props) =>
    props.disabled ? 'var(--color-light-gray)' : 'var(--base-white, #fff)'};
  flex-direction: ${(props) =>
    props.$layout === 'vertical' ? 'column' : 'row'};
  ${(props) => {
    if (props.active) {
      return `
        background: var(--red-100-selected-bg, #fee2e2);
        border: 1px transparent !important;
        border-radius: 4px;
        color: var(--red-700-button-accent, #b91c1c);
        `;
    } else {
      return `
      `;
    }
  }};

  border-radius: 4px;
  border: 1px solid var(--color-gray, #a0a0a0);
  div.active {
    border: 1px transparent;
    background: var(--red-100-selected-bg, #fee2e2);
    border-radius: 4px;
    div.singleValue {
      color: var(--red-700-button-accent, #b91c1c);
    }
    div.indicatorsContainer {
      svg {
        color: var(--red-700-button-accent, #b91c1c);
        opacity: 0.8;
      }
    }
  }

  display: flex;
  flex-direction: row;
  align-items: center;
  div.container {
    width: 100%;
  }
  div.icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    svg {
      height: 24px;
      width: 24px;
    }
  }

  div.control {
    border: 0;
    box-shadow: none;
    background-color: ${(props) =>
      props.disabled ? 'var(--color-light-gray) !important' : 'transparent'};
    border: ${(props) =>
      props.disabled ? 'var(--border-disabled) !important' : 'transparent'};
    ${(props) => {
      switch (props.$size) {
        case 'L':
          return `
          padding: 8px !important; 
        `;
        case 'M':
          return `
          padding: 4px !important; 
        `;
        case 'S':
          return `
          padding: 0; 
        `;
        default:
          return '';
      }
    }}
    svg {
      fill: ${(props) => (props.disabled ? 'var(--color-placeholder)' : '')};
    }
  }

  div.option {
    background: white;
    line-height: 28px;
    gap: 12px;
    display: flex;
    align-items: center;
  }

  div.option:hover {
    background: var(--bg-tertiary-gray-100, #f4f4f5);
  }
  div.option.selected {
    background: var(--red-100-selected-bg, #fee2e2);
    color: var(--red-700-button-accent, #b91c1c);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  div.menu {
    box-sizing: border-box;
    width: 100%;
    min-width: max-content;
    margin-top: 1px;
    color: var(--font-main, #333333);
    padding-left: 0px;
    @media (max-width: ${BREAKPOINT_SP}) {
      > div {
        width: calc(100vw - 54px);
      }
    }
  }
  div.menu.padding {
    margin-left: -28px;
    width: calc(100% + 28px);
    .option {
      padding-left: 36px;
    }
  }
  .hidden-on-mobile {
    display: none;
  }

  span.separator {
    display: none;
  }
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  font-family: 'Noto Sans JP';
  line-height: 1.75;
`;

const Container = styled.div<{ $layout?: string }>`
  display: flex;
  flex-direction: ${(props) =>
    props.$layout === 'vertical' ? 'column' : 'row'};
  gap: 8px;
  align-items: ${(props) =>
    props.$layout === 'vertical' ? 'flex-start' : 'center'};
`;

const ButtonContainer = styled.div<SelectWrapperProps>`
  flex-direction: ${(props) =>
    props.$layout === 'vertical' ? 'column' : 'row'};
  align-items: ${(props) =>
    props.$layout === 'vertical' ? 'flex-start' : 'center'};
  padding-left: ${(props) => (props.$size === 'L' ? '12px' : '8px')};
  display: flex;
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

export default Picklist;
